import { Component, OnInit, AfterViewInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { OciService } from '../../services/oci.service';
import { AppfileService } from '../../services/appfile.service';
import { AppFileModel } from '../../models/appfile.model';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-app-file-viewer',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule, Toast],
  templateUrl: './app-file-viewer.component.html',
  styleUrls: ['./app-file-viewer.component.css']
})
export class AppFileViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewer', { static: false }) viewerRef!: ElementRef;

  fileId: number = 0;
  currentFile: AppFileModel | null = null;

  webViewerInstance: WebViewerInstance | null = null;
  isWebViewerReady: boolean = false;
  isLoading: boolean = true;

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private ociService: OciService,
    private appfileService: AppfileService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const fileIdParam = params.get('fileId');
      if (fileIdParam) {
        this.fileId = parseInt(fileIdParam, 10);
      }
    });
  }

  ngAfterViewInit(): void {
    this.initializeAndLoad();
  }

  private async initializeAndLoad(): Promise<void> {
    try {
      this.isLoading = true;
      await this.initializeWebViewer();
      await this.loadFile();
    } catch (error) {
      console.error('Error initializing viewer:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to initialize viewer'
      });
      this.isLoading = false;
    }
  }

  private async initializeWebViewer(): Promise<void> {
    if (!this.viewerRef?.nativeElement) {
      console.error('Viewer container not found');
      return;
    }

    this.webViewerInstance = await WebViewer.Iframe(
      {
        path: 'assets/webviewer/lib',
        licenseKey: environment.apryseWebLicenseKey,
        enableAnnotations: true,
        enableMeasurement: true,
        enableRedaction: false,
      },
      this.viewerRef.nativeElement
    );

    await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000));

    this.isWebViewerReady = true;
  }

  private async loadFile(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.appfileService.getAppFileById(this.fileId).subscribe({
        next: (file: AppFileModel) => {
          this.currentFile = file;
          this.loadDocument(file);
          resolve();
        },
        error: (error) => {
          console.error('Error loading file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'File Not Found',
            detail: 'The requested file could not be found.'
          });
          this.isLoading = false;
          reject(error);
        }
      });
    });
  }

  private loadDocument(file: AppFileModel): void {
    if (!this.webViewerInstance || !this.isWebViewerReady) {
      console.error('WebViewer not ready');
      return;
    }

    const fileName = file.fileName || file.fileFullName || '';
    this.isLoading = true;

    this.ociService.getOciResourceEnhanced(file.filePath || '').subscribe({
      next: (blob: Blob) => {
        const blobUrl = URL.createObjectURL(blob);
        this.webViewerInstance!.UI.loadDocument(blobUrl, {
          filename: fileName,
          extension: this.getFileExtension(fileName)
        });
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error downloading file:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to load ${fileName}.`
        });
        this.isLoading = false;
      }
    });
  }

  private getFileExtension(fileName: string): string {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot > -1 ? fileName.substring(lastDot + 1).toLowerCase() : '';
  }

  ngOnDestroy(): void {
    if (this.webViewerInstance) {
      this.webViewerInstance = null;
    }
  }
}
